// InvestmentForm.tsx - Comprehensive investment create/edit form with theme support

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Investment, 
  InvestmentFormData,
  InvestmentValidationResult 
} from '../../types/Investment';
import { InvestmentService } from '../../services/InvestmentService';
import { useToast } from '../../hooks/useToast';
import { useThemeUtils } from '../../hooks/useThemeUtils';
import { StandardButton, FormField, SelectField, TextAreaField } from '../ui';

interface InvestmentFormProps {
  investment?: Investment;
  onSave: (investment: Investment) => void;
  onCancel: () => void;
  mode: 'create' | 'edit';
  scenarioMode?: boolean; // If true, don't save to global library
}

export const InvestmentForm: React.FC<InvestmentFormProps> = ({
  investment,
  onSave,
  onCancel,
  mode,
  scenarioMode = false
}) => {
  const { showToast } = useToast();
  const { backgroundPatterns, textPatterns, themeClass } = useThemeUtils();
  
  // Common styles for non-migrated elements
  
  const labelClasses = `block text-sm font-medium ${textPatterns.primary} mb-2`;
  // Removed unused errorClasses and warningClasses - using FormField built-in error/warning styling
  const inputClasses = themeClass({
    base: 'w-full px-3 py-2.5 border rounded-lg focus:outline-none focus:ring-2 focus:ring-offset-1 transition-all duration-200',
    light: 'bg-white border-slate-300 text-slate-900 placeholder:text-slate-500 focus:ring-professional-blue-500 focus:border-professional-blue-500 hover:border-slate-400',
    dark: 'bg-navy-700 border-navy-600 text-slate-200 placeholder:text-slate-400 focus:ring-professional-blue-400 focus:border-professional-blue-400 hover:border-navy-500'
  });

  const [saving, setSaving] = useState(false);
  const [validation] = useState<InvestmentValidationResult>({
    isValid: true,
    errors: {},
    warnings: {}
  });

  const [formData, setFormData] = useState<InvestmentFormData>({
    name: '',
    icon: '📦',
    description: '',
    longDescription: '',
    cost: 0,
    category: 'infrastructure',
    impact: {
      growth: 0,
      stability: 0,
      riskReduction: 0,
      socialBenefit: 0
    },
    tags: [],
    prerequisites: [],
    implementationTime: 30,
    riskLevel: 'medium',
    roi: 15,
    vendor: 'Internal',
    supportLevel: 'business-hours',
    isActive: true
  });

  const [newTag, setNewTag] = useState('');
  // Removed unused newPrerequisite state - prerequisites functionality not implemented in UI

  // Pre-populate form when editing
  useEffect(() => {
    if (investment && mode === 'edit') {
      setFormData({
        name: investment.name,
        icon: investment.icon,
        description: investment.description,
        longDescription: investment.longDescription || '',
        cost: investment.cost,
        category: investment.category,
        impact: investment.impact,
        tags: investment.tags || [],
        prerequisites: investment.prerequisites || [],
        implementationTime: investment.implementationTime || 30,
        riskLevel: investment.riskLevel || 'medium',
        roi: investment.roi || 15,
        vendor: investment.vendor || 'Internal',
        supportLevel: investment.supportLevel || 'business-hours',
        isActive: investment.isActive !== false
      });
    }
  }, [investment, mode]);

  const handleSubmit = async () => {
    // Validate required fields
    if (!formData.name?.trim()) {
      showToast({ title: 'Name is required', type: 'error' });
      return;
    }
    if (!formData.icon?.trim()) {
      showToast({ title: 'Icon is required', type: 'error' });
      return;
    }
    if (!formData.description?.trim()) {
      showToast({ title: 'Description is required', type: 'error' });
      return;
    }
    if (formData.cost < 0) {
      showToast({ title: 'Cost must be a positive number', type: 'error' });
      return;
    }
    
    try {
      setSaving(true);
      
      let savedInvestment: Investment;
      
      if (scenarioMode) {
        // In scenario mode, just create the investment object without saving to global library
        const validation = InvestmentService.validateInvestment(formData);
        if (!validation.isValid) {
          throw new Error(`Validation failed: ${Object.values(validation.errors).flat().join(', ')}`);
        }
        
        savedInvestment = {
          id: mode === 'edit' && investment ? investment.id : `inv-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
          ...formData,
          createdAt: mode === 'edit' && investment ? investment.createdAt : new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          version: mode === 'edit' && investment ? (investment.version || 1) + 1 : 1
        };
        
        showToast({ 
          title: mode === 'create' ? 'Investment configured' : 'Investment updated',
          type: 'success' 
        });
      } else {
        // Normal mode - save to global library
        if (mode === 'create') {
          savedInvestment = await InvestmentService.create(formData);
          showToast({ title: 'Investment created successfully', type: 'success' });
        } else {
          savedInvestment = await InvestmentService.update(investment!.id, formData);
          showToast({ title: 'Investment updated successfully', type: 'success' });
        }
      }
      
      onSave(savedInvestment);
    } catch (error) {
      showToast({
        title: error instanceof Error ? error.message : 'Error saving investment',
        type: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof InvestmentFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleImpactChange = (field: keyof Investment['impact'], value: number) => {
    setFormData(prev => ({
      ...prev,
      impact: { ...prev.impact, [field]: value }
    }));
  };

  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }));
      setNewTag('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }));
  };

  // Removed unused addPrerequisite function - prerequisites functionality not implemented in UI

  // Removed unused removePrerequisite function - prerequisites functionality not implemented in UI

  const emojiOptions = [
    '📦', '🎓', '🚨', '🖥️', '💾', '🛡️', '📋', '🔐', '🤖', '🔒',
    '🏗️', '💻', '🏥', '👥', '🌐', '📊', '🔧', '⚡', '📱', '🎯'
  ];

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  const tagClasses = themeClass({
    base: 'inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium',
    light: 'bg-professional-blue-100 text-professional-blue-800 border border-professional-blue-200',
    dark: 'bg-professional-blue-900/30 text-professional-blue-300 border border-professional-blue-700'
  });

  const tagButtonClasses = themeClass({
    base: 'hover:opacity-70 transition-opacity',
    light: 'text-professional-blue-600',
    dark: 'text-professional-blue-400'
  });

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`${backgroundPatterns.card} rounded-lg p-6 max-w-4xl mx-auto`}
    >
      <div className="mb-6">
        <h2 className={`text-2xl font-bold ${textPatterns.primary}`}>
          {mode === 'create' ? 'Create New Investment' : 'Edit Investment'}
        </h2>
        <p className={`${textPatterns.secondary} mt-1`}>
          {mode === 'create' 
            ? 'Add a new investment option to your scenario library'
            : 'Update investment details and configuration'
          }
        </p>
      </div>

      <div className="space-y-8">
        {/* Basic Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField
            label="Investment Name"
            value={formData.name}
            onChange={(e) => handleInputChange('name', e.target.value)}
            placeholder="e.g., Security Awareness Training"
            required
            error={validation.errors.name?.[0]}
          />

          <div>
            <label className={labelClasses}>
              Icon *
            </label>
            <div className="flex gap-2">
              <input
                type="text"
                value={formData.icon}
                onChange={(e) => handleInputChange('icon', e.target.value)}
                className={`flex-1 ${inputClasses}`}
                placeholder="📦"
                required
              />
              <div className="flex gap-1 flex-wrap max-w-xs">
                {emojiOptions.slice(0, 8).map(emoji => (
                  <button
                    key={emoji}
                    type="button"
                    onClick={() => handleInputChange('icon', emoji)}
                    className={themeClass({
                      base: 'p-2 rounded-lg text-lg transition-colors',
                      light: 'hover:bg-slate-100',
                      dark: 'hover:bg-navy-600'
                    })}
                  >
                    {emoji}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Description */}
        <TextAreaField
          label="Short Description"
          value={formData.description}
          onChange={(e) => handleInputChange('description', e.target.value)}
          rows={2}
          placeholder="Brief description of the investment..."
          required
          error={validation.errors.description?.[0]}
        />

        <TextAreaField
          label="Detailed Description"
          value={formData.longDescription || ''}
          onChange={(e) => handleInputChange('longDescription', e.target.value)}
          rows={4}
          placeholder="Detailed description including benefits, implementation details, etc..."
        />

        {/* Cost and Category */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <FormField
            label={`Cost ${formData.cost > 0 ? `(${formatCurrency(formData.cost)})` : ''}`}
            type="number"
            value={formData.cost}
            onChange={(e) => handleInputChange('cost', parseInt(e.target.value) || 0)}
            placeholder="100000"
            min="0"
            step="1000"
            required
            warning={validation.warnings.cost?.[0]}
          />

          <SelectField
            label="Category"
            value={formData.category}
            onChange={(e) => handleInputChange('category', e.target.value)}
            options={[
              { value: 'infrastructure', label: 'Infrastructure' },
              { value: 'technology', label: 'Technology' },
              { value: 'education', label: 'Education' },
              { value: 'healthcare', label: 'Healthcare' },
              { value: 'defense', label: 'Defense' },
              { value: 'social', label: 'Social' }
            ]}
            required
          />

          <SelectField
            label="Risk Level"
            value={formData.riskLevel}
            onChange={(e) => handleInputChange('riskLevel', e.target.value)}
            options={[
              { value: 'low', label: 'Low' },
              { value: 'medium', label: 'Medium' },
              { value: 'high', label: 'High' }
            ]}
          />
        </div>

        {/* Impact Values */}
        <div>
          <h3 className={`text-lg font-medium ${textPatterns.primary} mb-4`}>Impact Assessment</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(formData.impact).map(([key, value]) => (
              <div key={key}>
                <label className={`block text-sm font-medium ${textPatterns.primary} mb-2 capitalize`}>
                  {key.replace(/([A-Z])/g, ' $1').trim()} ({value}%)
                </label>
                <input
                  type="range"
                  min="0"
                  max="100"
                  value={value}
                  onChange={(e) => handleImpactChange(key as keyof Investment['impact'], parseInt(e.target.value))}
                  className={themeClass({
                    base: 'w-full h-2 rounded-lg appearance-none cursor-pointer',
                    light: 'bg-slate-200 slider-thumb:bg-professional-blue-600',
                    dark: 'bg-navy-600 slider-thumb:bg-professional-blue-400'
                  })}
                />
                <div className={`flex justify-between text-xs ${textPatterns.tertiary} mt-1`}>
                  <span>0%</span>
                  <span>100%</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Tags */}
        <div>
          <label className={labelClasses}>
            Tags
          </label>
          <div className="flex gap-2 mb-3">
            <input
              type="text"
              value={newTag}
              onChange={(e) => setNewTag(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
              className={`flex-1 ${inputClasses}`}
              placeholder="Add a tag..."
            />
            <StandardButton
              type="button"
              onClick={addTag}
              variant="primary"
              size="small"
            >
              Add
            </StandardButton>
          </div>
          <div className="flex flex-wrap gap-2">
            {formData.tags.map(tag => (
              <span key={tag} className={tagClasses}>
                {tag}
                <button
                  type="button"
                  onClick={() => removeTag(tag)}
                  className={tagButtonClasses}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>

        {/* Form Actions */}
        <div className={`flex gap-4 pt-6 border-t ${themeClass({ light: 'border-slate-200', dark: 'border-navy-600' })}`}>
          <StandardButton
            onClick={handleSubmit}
            disabled={saving}
            isLoading={saving}
            variant="primary"
            fullWidth
          >
            {mode === 'create' ? 'Create Investment' : 'Update Investment'}
          </StandardButton>
          <StandardButton
            onClick={onCancel}
            variant="secondary"
            fullWidth
          >
            Cancel
          </StandardButton>
        </div>
      </div>
    </motion.div>
  );
};